import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useSWRConfig } from 'swr';

import { StudentRemovedModal } from '~/classes/[classId]/components/studentRemovedModal';
import { useRealtimeClass } from '~/lib/realtime/useRealtimeClass';
import { ApiSdkSwrKey } from '~/lib/swr';
import { useAddLayer } from '~/providers/layerProvider';

import { useCurrentStatusSender } from '../[classId]/currentStatus/hooks';

interface StudentInClass {
  studentInClassId: string;
}

interface Config<T extends StudentInClass> {
  backPath?: string;
  studentInClassId?: string;
  students: T[] | undefined;
  swrKey: ApiSdkSwrKey;
}

export const useRosterChangeActions = <T extends StudentInClass>({
  backPath,
  studentInClassId: studentInClassIdOverride,
  students,
  swrKey,
}: Config<T>): void => {
  const { mutate } = useSWRConfig();
  const { pathname } = useLocation();
  const { classId, studentInClassId: studentInClassIdParams } = useParams<{
    classId: string;
    studentInClassId: string;
  }>();
  const studentInClassId = studentInClassIdOverride ?? studentInClassIdParams;
  const { notifyNewStudentInClass } = useCurrentStatusSender(classId);
  const isRoster = pathname.includes('roster');

  const onConnected = async (): Promise<void> => {
    if (!isRoster) return;

    await mutate(swrKey);
  };

  useRealtimeClass(
    classId ?? '',
    async message => {
      if (
        message.type !== 'removedStudentInClass' &&
        message.type !== 'newStudentInClass'
      ) {
        return;
      }

      await mutate(swrKey);

      notifyNewStudentInClass();
    },
    onConnected,
  );

  const { open } = useAddLayer(props => (
    <StudentRemovedModal {...props} backPath={backPath} />
  ));

  useEffect(() => {
    if (!students || !backPath) return;

    const foundStudent = students.find(
      student => student.studentInClassId === studentInClassId,
    );

    if (!studentInClassId || foundStudent) return;

    open();
  }, [backPath, open, studentInClassId, students]);
};
